/**
 * The project form's side panel in the URL (`ProjectFormDrawer`, `useProjectForm`) — pure, unit-tested, and light: the
 * routes import it (the form's old pages redirect here), so it pulls in nothing but types.
 */
import type { LocationQuery, LocationQueryRaw, RouteLocationRaw } from 'vue-router'

/** What a new form creates: a project, or a template (PM-5's "New template", WAVE1 Q13). */
export type ProjectFormKind = 'project' | 'template'

/** The old address `/pm/projects/new?kind=template` asked for a template; anything else, a project. */
export function formKind(query: Readonly<Record<string, unknown>>): ProjectFormKind {
  return query.kind === 'template' ? 'template' : 'project'
}

/**
 * What the project form's side panel shows (`ProjectFormDrawer`, `useProjectForm`), kept in the URL of the page under
 * it: `?new=project` (`&template=<id>`: that template preselected), `?new=template`, `?edit=<id>`.
 */
export type ProjectFormRequest =
  { mode: 'new'; kind: ProjectFormKind; templateId: number | null } | { mode: 'edit'; id: number }

const FORM_KEYS: readonly string[] = ['new', 'edit', 'template']

const firstValue = (value: LocationQuery[string] | undefined): string | null =>
  (Array.isArray(value) ? value[0] : value) ?? null

function positiveId(value: string | null): number | null {
  const id = Number(value)
  return value && Number.isInteger(id) && id > 0 ? id : null
}

export function readProjectForm(query: LocationQuery): ProjectFormRequest | null {
  const edit = positiveId(firstValue(query.edit))
  if (edit) return { mode: 'edit', id: edit }
  const kind = firstValue(query.new)
  if (kind === 'template') return { mode: 'new', kind: 'template', templateId: null }
  if (kind === 'project') return { mode: 'new', kind: 'project', templateId: positiveId(firstValue(query.template)) }
  return null
}

/** The page's query with the panel's keys set for `request` (`null`: the panel closed); the other keys are kept. */
export function projectFormQuery(
  query: LocationQuery | LocationQueryRaw,
  request: ProjectFormRequest | null,
): LocationQueryRaw {
  const rest = Object.fromEntries(Object.entries(query).filter(([key]) => !FORM_KEYS.includes(key)))
  if (!request) return rest
  if (request.mode === 'edit') return { ...rest, edit: String(request.id) }
  if (request.kind === 'template') return { ...rest, new: 'template' }
  return request.templateId
    ? { ...rest, new: 'project', template: String(request.templateId) }
    : { ...rest, new: 'project' }
}

/**
 * The form's old pages (`/pm/projects/new`, `?template=<id>`, `?kind=template`, `/pm/projects/new-template`): the list
 * with the panel open — the templates listed for a new template.
 */
export function newFormRedirect(query: LocationQuery, kind: ProjectFormKind = formKind(query)): RouteLocationRaw {
  const { kind: _kind, ...rest } = query
  void _kind
  if (kind === 'template') {
    return {
      name: 'projects',
      query: { ...projectFormQuery(rest, { mode: 'new', kind, templateId: null }), status: 'templates' },
    }
  }
  const templateId = positiveId(firstValue(query.template))
  return { name: 'projects', query: projectFormQuery(rest, { mode: 'new', kind, templateId }) }
}
